import { motion } from "framer-motion";
import { SectionHead, FadeUp, EASE } from "./Reveal";
import { openQuote } from "../lib/lenis";

const SERVICES = [
  {
    key: "corporate",
    badge: "Most requested",
    title: "Corporate Wellness",
    desc: "On-site massage and therapeutic bodywork for companies — wellness days, office programs and teams that run on deadline. Your people stay; their nervous systems reset.",
    points: ["Employee wellness days", "Desk-posture recovery", "Executive care"],
    img: "/images/corporate-wellness.png",
    alt: "Ariane Kamba delivering on-site chair massage in a corporate wellness setting",
    span: "lg:col-span-4",
    h: "h-56 lg:h-64",
  },
  {
    key: "events",
    badge: "Chair massage since 2021",
    title: "Events & Conferences",
    desc: "Conference floors, launches, VIP experiences. Chair massage is the event favourite — delivered at major poker tournaments and events since 2021, including the World Series of Poker (WSOP). A professional bodywork station that becomes the most talked-about booth in the room.",
    points: ["Chair massage stations", "World Series of Poker (WSOP)", "Launches & VIP"],
    img: "https://images.unsplash.com/photo-1692137211414-d07156515813?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Event stage lighting",
    span: "lg:col-span-2",
    h: "h-56 lg:h-64",
  },
  {
    key: "touring",
    badge: null,
    title: "Touring & Production",
    desc: "Pre-show tune-ups, post-show recovery, tour-long care for artists, dancers, crews — in the most demanding production environments there are.",
    points: ["Pre & post show care", "Crew recovery"],
    img: "/images/t_postmalone.jpg",
    alt: "Backstage on tour",
    span: "lg:col-span-2",
    h: "h-56 lg:h-64",
  },
  {
    key: "private",
    badge: "Also: Holistic Facelift",
    title: "Private Mobile Sessions",
    desc: "Therapeutic bodywork brought to your home, hotel or workplace. Facial sculpting and buccal massage available — no needles, no downtime.",
    points: ["Home · hotel · office", "Facial sculpting & buccal"],
    img: "/images/ariane-ball.jpg",
    alt: "Ariane Kamba — private mobile bodywork session",
    span: "lg:col-span-4",
    h: "h-56 lg:h-64",
  },
];

export const Services = () => (
  <section id="services" className="relative py-24 lg:py-32" data-testid="services-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead
        eyebrow="Services"
        title={<>Four ways to put elite bodywork <em className="italic text-gold">to work</em>.</>}
        sub="One multidisciplinary practitioner, working from the fascia out — RMT-grade care, stretch therapy, nervous-system regulation — deployed wherever your people perform."
      />
      <div className="mt-14 grid grid-cols-1 lg:grid-cols-6 gap-5">
        {SERVICES.map((s, i) => (
          <motion.button
            key={s.key}
            data-testid={`service-card-${s.key}`}
            onClick={() => openQuote(s.key)}
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
            className={`${s.span} group relative overflow-hidden rounded-2xl border border-white/10 bg-ink2 text-left transition-all duration-500 hover:border-gold/40 hover:shadow-[0_20px_60px_rgba(212,175,55,0.08)]`}
          >
            <div className={`relative ${s.h} overflow-hidden`}>
              <img src={s.img} alt={s.alt} loading="lazy"
                className={`h-full w-full object-cover transition-transform duration-700 group-hover:scale-105 ${s.key === "private" ? "object-[50%_12%]" : ""} ${s.key === "corporate" ? "object-[50%_25%]" : ""}`} />
              <div className="absolute inset-0 bg-gradient-to-t from-ink2 via-ink2/30 to-transparent" aria-hidden="true" />
              {s.badge && (
                <span className="absolute top-4 left-4 rounded-full bg-gold/15 backdrop-blur border border-gold/40 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-gold">
                  {s.badge}
                </span>
              )}
            </div>
            <div className="p-6 lg:p-7">
              <div className="flex items-start justify-between gap-4">
                <h3 className="font-serif text-2xl text-ivory font-light">{s.title}</h3>
                <span className="mt-1.5 inline-block text-gold transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" aria-hidden="true">↗</span>
              </div>
              <p className="mt-3 text-sm text-mute leading-relaxed">{s.desc}</p>
              <ul className="mt-4 flex flex-wrap gap-2">
                {s.points.map((p) => (
                  <li key={p} className="rounded-full border border-white/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-mute">
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </motion.button>
        ))}
      </div>

      <FadeUp delay={0.05} className="mt-16">
        <div className="relative overflow-hidden rounded-2xl border border-gold/25 bg-gradient-to-b from-gold/10 via-transparent to-transparent p-6 sm:p-10" data-testid="corporate-options-panel">
          <span className="pointer-events-none absolute -top-6 right-2 font-serif text-[7rem] leading-none text-gold/5 select-none" aria-hidden="true">✳</span>
          <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-ivory text-center max-w-3xl mx-auto" data-testid="corporate-options-title">
            Wellness in Motion — <em className="italic text-gold">Corporate Massage Options</em>
          </h3>
          <div className="mt-9 grid md:grid-cols-2 gap-5 items-start">
            <div className="h-full rounded-xl border border-gold/35 bg-ink/70 p-7" data-testid="corporate-format-table">
              <span className="inline-block rounded-full bg-gold/15 border border-gold/40 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-gold">
                Signature Corporate Favourite
              </span>
              <h4 className="mt-4 font-serif text-2xl text-ivory font-light">
                Format 1 — Fascia Stretch Massage on the Table
              </h4>
              <p className="mt-3 text-sm text-mute leading-relaxed">
                The fascia stretch massage is performed fully clothed — no undressing required.
                The client is stretched and unwound while remaining comfortable and appropriately
                covered. This format is especially effective for releasing tension in the neck,
                shoulders, back, and surrounding areas, leaving people feeling physically open,
                relaxed, and unwound.
              </p>
              <p className="mt-5 font-mono text-[9px] uppercase tracking-[0.2em] text-gold">Why it works for corporate</p>
              <ul className="mt-3 space-y-2.5">
                {[
                  "Fully clothed and professional",
                  "Convenient and easy to integrate into workplace wellness events",
                  "Effective in a relatively short session",
                  "Accessible to employees who may not be comfortable with traditional massage",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-ivory/85">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            <div className="h-full rounded-xl border border-white/10 bg-ink/70 p-7" data-testid="corporate-format-chair">
              <span className="inline-block rounded-full border border-white/15 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-mute">
                Built for high volume
              </span>
              <h4 className="mt-4 font-serif text-2xl text-ivory font-light">
                Format 2 — Chair Massage
              </h4>
              <p className="mt-3 text-sm text-mute leading-relaxed">
                Focused chair massage designed for corporate environments and shorter sessions.
                Targets the neck, shoulders, upper back, and arms — the areas that commonly hold
                workplace tension. Ideal for events where many employees need to be treated within
                a limited time.
              </p>
              <ul className="mt-5 space-y-2.5">
                {[
                  "Targets neck, shoulders, upper back & arms",
                  "Short sessions, steady flow",
                  "Perfect for large teams and busy event floors",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-ivory/85">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-6 rounded-xl border border-white/10 bg-ink/60 p-5 text-sm text-ivory/85 leading-relaxed" data-testid="corporate-formats-note">
            <span className="text-gold font-medium">A note:</span> the table option is not
            traditional massage — the fully clothed fascia stretching approach makes it uniquely
            suited to corporate wellness, while chair massage provides a faster, high-volume option
            for larger events.
          </p>

          <div className="mt-6 rounded-xl border border-gold/40 bg-gradient-to-r from-gold/15 via-gold/5 to-transparent p-6 sm:p-7 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-5" data-testid="corporate-format-euphoria">
            <div>
              <span className="inline-block rounded-full bg-gold/15 border border-gold/40 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-gold">
                Premium group experience
              </span>
              <h4 className="mt-3 font-serif text-2xl text-ivory font-light">
                Euphoria — The Restorative Experience
              </h4>
              <p className="mt-2 text-sm text-mute leading-relaxed max-w-2xl">
                Our signature one-hour group session: guided stretching, breathwork and meditation
                in one seamless, fascia-rooted flow. Your team leaves unwound, clear and genuinely
                high on life.
              </p>
            </div>
            <a
              href="mailto:akamba81@gmail.com?subject=Euphoria%20for%20My%20Team"
              data-testid="corporate-euphoria-cta"
              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 font-mono text-[10px] uppercase tracking-[0.18em] font-medium text-ink transition-all duration-300 hover:bg-ivory hover:shadow-[0_0_44px_rgba(212,175,55,0.4)]"
            >
              Book Euphoria →
            </a>
          </div>
        </div>
      </FadeUp>

      <FadeUp delay={0.1} className="mt-10">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute">
          Not sure which fits? <button data-testid="services-quote-link" onClick={() => openQuote("corporate")} className="text-gold underline underline-offset-4 hover:text-ivory transition-colors">Tell me about your team</button> — I'll map it.
        </p>
      </FadeUp>
    </div>
  </section>
);
